"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import { getSubmissionCount, getTrophyArtworkCount } from "@/helpers/artist-activity.helpers";
import { useArtistPreviewModal, type ArtistPreviewTab } from "@/hooks/useArtistPreviewModal";
import { useWaveCreatorPreviewModal } from "@/hooks/useWaveCreatorPreviewModal";
import { closeAllCustomTooltips } from "@/helpers/tooltip.helpers";
import { ArtistActivityBadge } from "./ArtistActivityBadge";
import { ArtistPreviewModal } from "./ArtistPreviewModal";
import { WaveCreatorBadge } from "./WaveCreatorBadge";
import { WaveCreatorPreviewModal } from "./WaveCreatorPreviewModal";
import type { ApiProfileClassification } from "@/generated/models/ApiProfileClassification";
interface DropAuthorBadgesProfile {
  readonly id?: string | null;
  readonly handle?: string | null;
  readonly pfp?: string | null;
  readonly banner1_color?: string | null;
  readonly banner2_color?: string | null;
  readonly cic?: number;
  readonly rep?: number;
  readonly tdh?: number;
  readonly tdh_rate?: number;
  readonly xtdh?: number;
  readonly xtdh_rate?: number;
  readonly level?: number;
  readonly primary_address?: string | null;
  readonly primary_wallet?: string | null;
  readonly archived?: boolean;
  readonly active_main_stage_submission_ids?: readonly string[] | null;
  readonly winner_main_stage_drop_ids?: readonly string[] | null;
  readonly artist_of_prevote_cards?: readonly number[] | null;
  readonly profile_wave_id?: string | null;
  readonly is_wave_creator?: boolean | null;
  readonly badges?: {
    readonly artist_of_main_stage_submissions?: number | null;
    readonly artist_of_memes?: number | null;
    readonly profile_wave_id?: string | null;
  } | null;
  readonly classification: ApiProfileClassification;
  readonly sub_classification: string | null;
}
type ApiProfileMinWithAuthorBadges = ApiProfileMin & {
  readonly badges?: DropAuthorBadgesProfile["badges"];
};
interface DropAuthorBadgesProps {
  readonly profile: DropAuthorBadgesProfile;
  readonly tooltipIdPrefix?: string | undefined;
  readonly className?: string | undefined;
  readonly size?: "default" | "compact" | undefined;
  readonly onArtistPreviewOpen?: ((params: {
    readonly user: ApiProfileMin;
    readonly initialTab: ArtistPreviewTab;
  }) => void) | undefined;
  readonly onWaveCreatorPreviewOpen?: ((user: ApiProfileMin) => void) | undefined;
}
const DEFAULT_CONTAINER_CLASS = "tw-inline-flex tw-items-center tw-gap-x-1.5";
const getProfileWaveId = (profile: DropAuthorBadgesProfile): string | null => profile.profile_wave_id ?? profile.badges?.profile_wave_id ?? null;
const toApiProfileMin = (profile: DropAuthorBadgesProfile): ApiProfileMinWithAuthorBadges => {
  const primaryAddress = profile.primary_address ?? profile.primary_wallet ?? "";
  const fallbackId = primaryAddress;
  const profileWaveId = getProfileWaveId(profile);
  return {
    id: profile.id ?? fallbackId,
    handle: profile.handle ?? null,
    pfp: profile.pfp ?? null,
    banner1_color: profile.banner1_color ?? null,
    banner2_color: profile.banner2_color ?? null,
    cic: profile.cic ?? 0,
    rep: profile.rep ?? 0,
    tdh: profile.tdh ?? 0,
    tdh_rate: profile.tdh_rate ?? 0,
    xtdh: profile.xtdh ?? 0,
    xtdh_rate: profile.xtdh_rate ?? 0,
    level: profile.level ?? 0,
    primary_address: primaryAddress,
    subscribed_actions: [],
    archived: profile.archived ?? false,
    active_main_stage_submission_ids: profile.active_main_stage_submission_ids !== null && profile.active_main_stage_submission_ids !== undefined ? [...profile.active_main_stage_submission_ids] : [],
    winner_main_stage_drop_ids: profile.winner_main_stage_drop_ids !== null && profile.winner_main_stage_drop_ids !== undefined ? [...profile.winner_main_stage_drop_ids] : [],
    artist_of_prevote_cards: profile.artist_of_prevote_cards !== null && profile.artist_of_prevote_cards !== undefined ? [...profile.artist_of_prevote_cards] : [],
    profile_wave_id: profileWaveId,
    is_wave_creator: profile.is_wave_creator === true || profileWaveId !== null,
    classification: profile.classification,
    sub_classification: profile.sub_classification,
    badges: profile.badges
  };
};
export const DropAuthorBadges: React.FC<DropAuthorBadgesProps> = t0 => {
  const $ = _c(46);
  const {
    profile,
    tooltipIdPrefix: t1,
    className: t2,
    size: t3,
    onArtistPreviewOpen,
    onWaveCreatorPreviewOpen
  } = t0;
  const tooltipIdPrefix = t1 === undefined ? "author-badges" : t1;
  const className = t2 === undefined ? DEFAULT_CONTAINER_CLASS : t2;
  const size = t3 === undefined ? "default" : t3;
  const submissionCount = getSubmissionCount(profile);
  const trophyCount = getTrophyArtworkCount(profile);
  const hasActivityBadge = submissionCount > 0 || trophyCount > 0;
  let t4;
  if ($[0] !== profile) {
    t4 = profile.is_wave_creator === true || getProfileWaveId(profile) !== null;
    $[0] = profile;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const isWaveCreator = t4;
  let t5;
  if ($[2] !== profile) {
    t5 = toApiProfileMin(profile);
    $[2] = profile;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const modalUser = t5;
  const {
    isModalOpen: isArtistPreviewOpen,
    activeTab,
    handleBadgeClick: handleArtistBadgeClick,
    handleTabChange: handleArtistTabChange,
    handleModalClose: handleArtistModalClose
  } = useArtistPreviewModal();
  const {
    isModalOpen: isWaveCreatorPreviewOpen,
    handleBadgeClick: handleWaveCreatorBadgeClick,
    handleModalClose: handleWaveCreatorModalClose
  } = useWaveCreatorPreviewModal();
  let t6;
  if ($[4] !== handleArtistBadgeClick || $[5] !== modalUser || $[6] !== onArtistPreviewOpen) {
    t6 = tab => {
      closeAllCustomTooltips();
      if (onArtistPreviewOpen) {
        onArtistPreviewOpen({
          user: modalUser,
          initialTab: tab
        });
        return;
      }
      handleArtistBadgeClick(tab);
    };
    $[4] = handleArtistBadgeClick;
    $[5] = modalUser;
    $[6] = onArtistPreviewOpen;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const onArtistBadgeClick = t6;
  let t7;
  if ($[8] !== handleWaveCreatorBadgeClick || $[9] !== modalUser || $[10] !== onWaveCreatorPreviewOpen) {
    t7 = () => {
      closeAllCustomTooltips();
      if (onWaveCreatorPreviewOpen) {
        onWaveCreatorPreviewOpen(modalUser);
        return;
      }
      handleWaveCreatorBadgeClick();
    };
    $[8] = handleWaveCreatorBadgeClick;
    $[9] = modalUser;
    $[10] = onWaveCreatorPreviewOpen;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const onWaveCreatorBadgeClick = t7;
  if (!hasActivityBadge && !isWaveCreator) {
    return null;
  }
  let t8;
  if ($[12] !== hasActivityBadge || $[13] !== onArtistBadgeClick || $[14] !== size || $[15] !== submissionCount || $[16] !== tooltipIdPrefix || $[17] !== trophyCount) {
    t8 = hasActivityBadge && <ArtistActivityBadge submissionCount={submissionCount} trophyCount={trophyCount} onBadgeClick={onArtistBadgeClick} tooltipId={`${tooltipIdPrefix}-activity`} size={size} />;
    $[12] = hasActivityBadge;
    $[13] = onArtistBadgeClick;
    $[14] = size;
    $[15] = submissionCount;
    $[16] = tooltipIdPrefix;
    $[17] = trophyCount;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== isWaveCreator || $[20] !== onWaveCreatorBadgeClick || $[21] !== size || $[22] !== tooltipIdPrefix) {
    t9 = isWaveCreator && <WaveCreatorBadge tooltipId={`${tooltipIdPrefix}-wave-creator`} onBadgeClick={onWaveCreatorBadgeClick} size={size} />;
    $[19] = isWaveCreator;
    $[20] = onWaveCreatorBadgeClick;
    $[21] = size;
    $[22] = tooltipIdPrefix;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== className || $[25] !== t8 || $[26] !== t9) {
    t10 = <div className={className}>{t8}{t9}</div>;
    $[24] = className;
    $[25] = t8;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== activeTab || $[29] !== handleArtistModalClose || $[30] !== handleArtistTabChange || $[31] !== hasActivityBadge || $[32] !== isArtistPreviewOpen || $[33] !== modalUser || $[34] !== onArtistPreviewOpen) {
    t11 = hasActivityBadge && !onArtistPreviewOpen && <ArtistPreviewModal isOpen={isArtistPreviewOpen} onClose={handleArtistModalClose} user={modalUser} activeTab={activeTab} onTabChange={handleArtistTabChange} />;
    $[28] = activeTab;
    $[29] = handleArtistModalClose;
    $[30] = handleArtistTabChange;
    $[31] = hasActivityBadge;
    $[32] = isArtistPreviewOpen;
    $[33] = modalUser;
    $[34] = onArtistPreviewOpen;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  let t12;
  if ($[36] !== handleWaveCreatorModalClose || $[37] !== isWaveCreator || $[38] !== isWaveCreatorPreviewOpen || $[39] !== modalUser || $[40] !== onWaveCreatorPreviewOpen) {
    t12 = isWaveCreator && !onWaveCreatorPreviewOpen && <WaveCreatorPreviewModal isOpen={isWaveCreatorPreviewOpen} onClose={handleWaveCreatorModalClose} user={modalUser} />;
    $[36] = handleWaveCreatorModalClose;
    $[37] = isWaveCreator;
    $[38] = isWaveCreatorPreviewOpen;
    $[39] = modalUser;
    $[40] = onWaveCreatorPreviewOpen;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== t10 || $[43] !== t11 || $[44] !== t12) {
    t13 = <>{t10}{t11}{t12}</>;
    $[42] = t10;
    $[43] = t11;
    $[44] = t12;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
};